/**
 * One screen's crash must not blank the product.
 *
 * WHY THIS EXISTS. The Parameters screen read `s?.byDomain[d]` — a guard on `s` but not on
 * `byDomain`, over a field the API never returned. React unmounted the entire tree, so the
 * result was not "Parameters is broken" but a WHITE PAGE: no navigation, no site selector,
 * no way to reach the eight modules that were working, and nothing on screen naming the
 * problem. An operator watching a fleet lost the console because one filter chip wanted a
 * count.
 *
 * The boundary is placed INSIDE the shell, around the routed view only, so the sidebar and
 * header survive and the operator can navigate away from the broken screen. A boundary
 * around the whole app would still take the navigation with it.
 *
 * Deliberately not a retry loop. Re-rendering the same component with the same props
 * reproduces the same throw, and an auto-retry turns one error into an infinite flicker.
 * The reset is explicit, and remounting is keyed on the tab so moving away and back is a
 * clean attempt rather than a stuck error card.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Icon } from '@/components/lumen/Icon';

interface Props {
  children: ReactNode;
  /** Changing this remounts the boundary — used to clear the error on navigation. */
  resetKey?: string;
  /** Named in the fallback so the operator can say which screen failed. */
  label?: string;
}

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

export class ViewErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ info });
    /*
     * Logged, not swallowed. The fallback tells the operator what happened; the console
     * is what tells an engineer WHERE, and a boundary that hides the stack makes the next
     * crash of this kind much harder to find than an unhandled throw would have been.
     */
    console.error(`[view] ${this.props.label ?? 'view'} crashed`, error, info.componentStack);
  }

  override componentDidUpdate(prev: Props): void {
    // A different screen is a fresh attempt. Without this the error card sticks until a
    // full reload, which is indistinguishable from the blank page it replaced.
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null, info: null });
    }
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const frame = (error.stack ?? '')
      .split('\n')
      .slice(1)
      .find((l) => l.includes('/src/'))
      ?.trim();

    return (
      <div className="p-[26px]">
        <div className="lm-card max-w-[760px] overflow-hidden">
          <div
            className="flex items-center gap-[8px] p-[13px_16px] text-[14px] font-bold"
            style={{ borderBottom: '1px solid var(--border)', color: 'var(--danger)' }}
          >
            <Icon name="ic-alert" size={16} />
            {this.props.label ? `${this.props.label} could not render` : 'This screen could not render'}
          </div>

          <div className="flex flex-col gap-[10px] p-[16px]">
            <p className="text-[13px] leading-[1.6]">
              The rest of the console is unaffected — the navigation on the left still works, and
              every other module is still reading live data. Only this screen stopped.
            </p>

            <div
              className="overflow-x-auto rounded-[var(--radius-sm)] p-[9px_11px]"
              style={{ background: 'var(--muted)' }}
            >
              <div
                className="lm-num whitespace-pre text-[11.5px]"
                style={{ color: 'var(--fg)' }}
              >
                {error.name}: {error.message}
              </div>
              {frame && (
                <div
                  className="lm-num mt-[4px] whitespace-pre text-[11px]"
                  style={{ color: 'var(--muted-fg)' }}
                >
                  {frame}
                </div>
              )}
            </div>

            <p className="text-[11.5px] leading-[1.55]" style={{ color: 'var(--muted-fg)' }}>
              The full stack is in the browser console. This message is shown verbatim rather
              than replaced with an apology, because the exact type error is the one piece of
              information that makes the fault findable.
            </p>

            <div className="flex gap-[7px]">
              <button
                type="button"
                onClick={() => this.setState({ error: null, info: null })}
                className="lm-press rounded-[var(--radius-sm)] px-[11px] py-[6px] text-[12.5px] font-bold"
                style={{ background: 'var(--brand)', color: 'var(--brand-fg)' }}
              >
                Try this screen again
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="lm-hov rounded-[var(--radius-sm)] px-[11px] py-[6px] text-[12.5px] font-semibold"
                style={{ border: '1px solid var(--border)' }}
              >
                Reload the console
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
